import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../api/supabaseClient";
import illustration from "../assets/login-background.svg";
import "./AuthPage.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://ektasia-backend.onrender.com";

const MIN_PASSWORD = 12;

/**
 * Supabase's messages are written for developers and some of them answer
 * questions a stranger should not get to ask -- whether an address has an
 * account, for instance. Map them to something a clinician can act on, and
 * fall back to a generic line rather than passing the raw text through.
 */
function readableError(error, mode) {
  const raw = (error?.message || "").toLowerCase();

  if (raw.includes("invalid login credentials")) {
    return "Email or password is incorrect.";
  }
  if (raw.includes("email not confirmed")) {
    return "Please confirm your email address first — check your inbox for the link.";
  }
  if (raw.includes("not been approved") || raw.includes("database error")) {
    return "This email has not been approved for access yet. Request access below and you'll hear back once it's reviewed.";
  }
  if (raw.includes("password")) {
    return `Password must be at least ${MIN_PASSWORD} characters and include upper case, lower case and a number.`;
  }
  if (raw.includes("rate limit") || raw.includes("too many")) {
    return "Too many attempts. Please wait a few minutes and try again.";
  }
  return mode === "signin"
    ? "Could not sign you in. Please check your details and try again."
    : "Something went wrong. Please try again.";
}

function passwordProblem(password) {
  if (password.length < MIN_PASSWORD) return `At least ${MIN_PASSWORD} characters.`;
  if (!/[a-z]/.test(password)) return "Include a lower case letter.";
  if (!/[A-Z]/.test(password)) return "Include an upper case letter.";
  if (!/[0-9]/.test(password)) return "Include a number.";
  return null;
}

export default function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup | request
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [practice, setPractice] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled && data?.session) navigate("/dashboard");
    });
    return () => { cancelled = true; };
  }, [navigate]);

  const switchTo = (next) => {
    setMode(next);
    setError("");
    setNotice("");
    setPassword("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);

    try {
      if (mode === "request") {
        const res = await fetch(`${API_BASE_URL}/access/request`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, practice: practice || null, note: note || null }),
        });
        const body = await res.json().catch(() => null);
        if (!res.ok) throw new Error(body?.detail || "Could not send the request");
        setNotice(body?.message || "Your request has been recorded.");
        setEmail(""); setPractice(""); setNote("");
        return;
      }

      if (mode === "signup") {
        const problem = passwordProblem(password);
        if (problem) { setError(problem); return; }
        const { error: signUpError } = await supabase.auth.signUp({ email, password });
        if (signUpError) throw signUpError;
        setNotice("Account created. Check your email for the confirmation link before signing in.");
        setPassword("");
        return;
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;
      // supabase-js persists the session itself; nothing to copy anywhere.
      navigate("/dashboard");
    } catch (err) {
      setError(readableError(err, mode));
    } finally {
      setBusy(false);
    }
  };

  const heading = { signin: "Sign in", signup: "Create your account", request: "Request access" }[mode];
  const action = { signin: "Sign in", signup: "Create account", request: "Send request" }[mode];

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-left">
          <h2>{heading}</h2>

          {mode === "signup" && (
            <p className="auth-lead">
              Accounts can only be created for approved addresses. If yours has not
              been approved yet, request access instead.
            </p>
          )}
          {mode === "request" && (
            <p className="auth-lead">
              Tell us who you are and we'll be in touch once your access has been
              reviewed.
            </p>
          )}

          <form onSubmit={submit}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              required
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
            />

            {mode !== "request" && (
              <input
                type="password"
                placeholder="Password"
                value={password}
                required
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}

            {mode === "signup" && (
              <p className="auth-hint">
                At least {MIN_PASSWORD} characters, with upper case, lower case and a number.
              </p>
            )}

            {mode === "request" && (
              <>
                <input
                  type="text"
                  placeholder="Practice or clinic (optional)"
                  value={practice}
                  maxLength={160}
                  onChange={(e) => setPractice(e.target.value)}
                />
                <textarea
                  placeholder="Anything we should know (optional)"
                  value={note}
                  maxLength={1000}
                  rows={3}
                  onChange={(e) => setNote(e.target.value)}
                />
              </>
            )}

            <button type="submit" disabled={busy}>
              {busy ? "Working…" : action}
            </button>
          </form>

          {error && <p className="error" role="alert">{error}</p>}
          {notice && <p className="notice" role="status">{notice}</p>}

          <div className="auth-switch">
            {mode !== "signin" && (
              <button type="button" onClick={() => switchTo("signin")}>
                Back to sign in
              </button>
            )}
            {mode !== "signup" && (
              <button type="button" onClick={() => switchTo("signup")}>
                Create your account
              </button>
            )}
            {mode !== "request" && (
              <button type="button" onClick={() => switchTo("request")}>
                Request access
              </button>
            )}
          </div>
        </div>

        <div className="auth-right">
          <img src={illustration} alt="" />
        </div>
      </div>
    </div>
  );
}
